import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import * as signalR from '@microsoft/signalr';
import { Container } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { addStatusToast, SectionHeader } from '../../common/components';
import { LOADING_STATUS } from '../../common/constants';
import signalRDac7Service from '../../common/hubs/signalRDac7Service';
import {
  clearProcessingProgress,
  createDac7ReportAsync,
  fetchDac7ReportsAsync,
  fetchWinTimeImportsAsync,
  generateDac7XmlAsync,
  importWinTimeDataAsync,
  markDac7ReportAsSubmittedAsync,
  processWinTimeDataAsync,
  resetCreateStatus,
  resetGenerateXmlStatus,
  resetImportStatus,
  resetMarkAsSubmittedStatus,
  resetProcessStatus,
  selectDac7CreateStatus,
  selectDac7Error,
  selectDac7GenerateXmlStatus,
  selectDac7ImportStatus,
  selectDac7IsProcessingInBackground,
  selectDac7LastXmlResult,
  selectDac7LoadingStatus,
  selectDac7MarkAsSubmittedStatus,
  selectDac7ProcessingComplete,
  selectDac7ProcessingProgress,
  selectDac7ProcessStatus,
  selectDac7Reports,
  selectDac7WinTimeImports,
  selectDac7WinTimeImportsStatus,
  setProcessingComplete,
  setProcessingProgress,
} from './dac7Slice';
import CreateReportCard from './components/CreateReportCard';
import GenerateXmlCard from './components/GenerateXmlCard';
import ImportWinTimeCard from './components/ImportWinTimeCard';
import MarkSubmittedCard from './components/MarkSubmittedCard';
import PreviousReportsCard from './components/PreviousReportsCard';
import ProcessWinTimeCard from './components/ProcessWinTimeCard';
import WinTimeImportsTable from './components/WinTimeImportsTable';
import {
  formatDate,
  getImportStatusBadge,
  getStatusBadge,
} from './dac7ViewUtils';

function Dac7Reports() {
  const dispatch = useAppDispatch();
  const reports = useAppSelector(selectDac7Reports);
  const winTimeImports = useAppSelector(selectDac7WinTimeImports);
  const loadingStatus = useAppSelector(selectDac7LoadingStatus);
  const createStatus = useAppSelector(selectDac7CreateStatus);
  const importStatus = useAppSelector(selectDac7ImportStatus);
  const processStatus = useAppSelector(selectDac7ProcessStatus);
  const winTimeImportsStatus = useAppSelector(selectDac7WinTimeImportsStatus);
  const generateXmlStatus = useAppSelector(selectDac7GenerateXmlStatus);
  const markAsSubmittedStatus = useAppSelector(selectDac7MarkAsSubmittedStatus);
  const lastXmlResult = useAppSelector(selectDac7LastXmlResult);
  const error = useAppSelector(selectDac7Error);
  const processingProgress = useAppSelector(selectDac7ProcessingProgress);
  const processingComplete = useAppSelector(selectDac7ProcessingComplete);
  const isProcessingInBackground = useAppSelector(
    selectDac7IsProcessingInBackground,
  );

  const currentYear = new Date().getFullYear();
  const [reportingYear, setReportingYear] = useState<number>(currentYear - 1);
  const [notes, setNotes] = useState<string>('');
  const [selectedReportId, setSelectedReportId] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processReportId, setProcessReportId] = useState<string>('');
  const [generateReportId, setGenerateReportId] = useState<string>('');
  const [docTypeIndic, setDocTypeIndic] = useState<string>('OECD1');
  const [submitReportId, setSubmitReportId] = useState<string>('');
  const [submissionNotes, setSubmissionNotes] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dispatch(fetchDac7ReportsAsync());
  }, [dispatch]);

  useEffect(() => {
    if (
      signalRDac7Service.connection.state ===
      signalR.HubConnectionState.Disconnected
    ) {
      console.log('Starting DAC7 SignalR connection');
      signalRDac7Service.startConnection();
    }

    signalRDac7Service.onProgressUpdate = update => {
      dispatch(setProcessingProgress(update));
    };

    signalRDac7Service.onCompletionUpdate = update => {
      dispatch(setProcessingComplete(update));
      dispatch(fetchDac7ReportsAsync());
      if (processReportId) {
        dispatch(fetchWinTimeImportsAsync(processReportId));
      }
      if (update.success) {
        dispatch(
          addStatusToast({
            Title: 'Onnistui',
            Message: `WinTime-tiedot käsitelty onnistuneesti. Myyjiä: ${update.sellersCreated || 0}, Kohteita: ${update.propertiesCreated || 0}`,
            IsFailed: false,
          }),
        );
      } else {
        dispatch(
          addStatusToast({
            Title: 'Virhe',
            Message: update.message || 'WinTime-tietojen käsittely epäonnistui',
            IsFailed: true,
          }),
        );
      }
    };
  }, [dispatch, processReportId]);

  useEffect(() => {
    if (processReportId) {
      dispatch(fetchWinTimeImportsAsync(processReportId));
    }
  }, [dispatch, processReportId]);

  const handleCreateReport = async () => {
    try {
      const result = await dispatch(
        createDac7ReportAsync({ reportingYear, notes: notes || undefined }),
      ).unwrap();
      dispatch(
        addStatusToast({
          Title: 'Onnistui',
          Message: `DAC7-raportti vuodelle ${reportingYear} luotu onnistuneesti`,
          IsFailed: false,
        }),
      );
      dispatch(resetCreateStatus());
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'DAC7-raportin luonti epäonnistui';
      dispatch(
        addStatusToast({
          Title: 'Virhe',
          Message: errorMessage,
          IsFailed: true,
        }),
      );
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImportWinTime = async () => {
    if (!selectedReportId || !selectedFile) {
      dispatch(
        addStatusToast({
          Title: 'Virhe',
          Message: 'Valitse raportti ja tiedosto',
          IsFailed: true,
        }),
      );
      return;
    }

    try {
      const result = await dispatch(
        importWinTimeDataAsync({
          reportId: selectedReportId,
          file: selectedFile,
        }),
      ).unwrap();
      dispatch(
        addStatusToast({
          Title: 'Onnistui',
          Message: `WinTime-tiedosto tuotu onnistuneesti. ${result?.totalRows || 0} riviä käsitelty.`,
          IsFailed: false,
        }),
      );
      dispatch(resetImportStatus());
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'WinTime-tiedoston tuonti epäonnistui';
      dispatch(
        addStatusToast({
          Title: 'Virhe',
          Message: errorMessage,
          IsFailed: true,
        }),
      );
    }
  };

  const handleProcessWinTime = async () => {
    if (!processReportId) {
      dispatch(
        addStatusToast({
          Title: 'Virhe',
          Message: 'Valitse raportti käsiteltäväksi',
          IsFailed: true,
        }),
      );
      return;
    }

    dispatch(clearProcessingProgress());

    try {
      await dispatch(processWinTimeDataAsync(processReportId)).unwrap();
      dispatch(
        addStatusToast({
          Title: 'Käsittely aloitettu',
          Message:
            'WinTime-tietojen käsittely on käynnissä taustalla. Seuraa edistymistä palkista.',
          IsFailed: false,
        }),
      );
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'WinTime-tietojen käsittelyn aloitus epäonnistui';
      dispatch(
        addStatusToast({
          Title: 'Virhe',
          Message: errorMessage,
          IsFailed: true,
        }),
      );
      dispatch(clearProcessingProgress());
    }
  };

  const handleGenerateXml = async () => {
    if (!generateReportId) {
      dispatch(
        addStatusToast({
          Title: 'Virhe',
          Message: 'Valitse raportti XML:n generointia varten',
          IsFailed: true,
        }),
      );
      return;
    }

    try {
      const result = await dispatch(
        generateDac7XmlAsync({
          reportId: generateReportId,
          request: { docTypeIndic },
        }),
      ).unwrap();

      dispatch(
        addStatusToast({
          Title: 'Onnistui',
          Message: `DAC7 XML generoitu onnistuneesti. Versio: ${result.versionNumber}, Myyjiä: ${result.sellerCount}, Kohteita: ${result.propertyListingCount}`,
          IsFailed: false,
        }),
      );

      const blob = new Blob([result.xmlContent], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      dispatch(fetchDac7ReportsAsync());
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'XML:n generointi epäonnistui';
      dispatch(
        addStatusToast({
          Title: 'Virhe',
          Message: errorMessage,
          IsFailed: true,
        }),
      );
    }
  };

  const handleMarkAsSubmitted = async () => {
    if (!submitReportId) {
      dispatch(
        addStatusToast({
          Title: 'Virhe',
          Message: 'Valitse raportti merkittäväksi lähetetyksi',
          IsFailed: true,
        }),
      );
      return;
    }

    try {
      await dispatch(
        markDac7ReportAsSubmittedAsync({
          reportId: submitReportId,
          request: { notes: submissionNotes || undefined },
        }),
      ).unwrap();

      dispatch(
        addStatusToast({
          Title: 'Onnistui',
          Message: 'Raportti merkitty lähetetyksi Ilmoittimeen',
          IsFailed: false,
        }),
      );

      dispatch(resetMarkAsSubmittedStatus());
      setSubmitReportId('');
      setSubmissionNotes('');

      dispatch(fetchDac7ReportsAsync());
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Raportin merkitseminen lähetetyksi epäonnistui';
      dispatch(
        addStatusToast({
          Title: 'Virhe',
          Message: errorMessage,
          IsFailed: true,
        }),
      );
    }
  };

  const yearOptions = [] as number[];
  for (let year = currentYear; year >= currentYear - 5; year -= 1) {
    yearOptions.push(year);
  }

  const isProcessing =
    processStatus === LOADING_STATUS.LOADING || isProcessingInBackground;

  return (
    <>
      <Helmet>
        <title>DAC7-raportit</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Taloushallinto', 'DAC7-raportit']}
          Title="DAC7-raportit"
        />

        <CreateReportCard
          reportingYear={reportingYear}
          notes={notes}
          yearOptions={yearOptions}
          createStatus={createStatus}
          onReportingYearChange={setReportingYear}
          onNotesChange={setNotes}
          onCreate={handleCreateReport}
        />

        {reports.length > 0 && (
          <ImportWinTimeCard
            reports={reports}
            selectedReportId={selectedReportId}
            selectedFile={selectedFile}
            importStatus={importStatus}
            fileInputRef={fileInputRef}
            onSelectReportId={setSelectedReportId}
            onFileChange={handleFileChange}
            onImport={handleImportWinTime}
          />
        )}

        {reports.length > 0 && (
          <ProcessWinTimeCard
            reports={reports}
            processReportId={processReportId}
            winTimeImports={winTimeImports}
            winTimeImportsStatus={winTimeImportsStatus}
            isProcessing={isProcessing}
            processingProgress={processingProgress}
            processingComplete={processingComplete}
            onProcessReportChange={setProcessReportId}
            onProcess={handleProcessWinTime}
            onDismissProcessingComplete={() => {
              dispatch(clearProcessingProgress());
              dispatch(resetProcessStatus());
            }}
          />
        )}

        {winTimeImports.length > 0 && (
          <WinTimeImportsTable
            winTimeImports={winTimeImports}
            formatDate={formatDate}
            getImportStatusBadge={getImportStatusBadge}
          />
        )}

        {reports.length > 0 && (
          <GenerateXmlCard
            reports={reports}
            generateReportId={generateReportId}
            docTypeIndic={docTypeIndic}
            generateXmlStatus={generateXmlStatus}
            lastXmlResult={lastXmlResult}
            onReportChange={newReportId => {
              setGenerateReportId(newReportId);
              const selectedReport = reports.find(r => r.id === newReportId);
              if (
                selectedReport?.status === 'SubmittedToIlmoitin' &&
                docTypeIndic === 'OECD1'
              ) {
                setDocTypeIndic('OECD2');
              }
            }}
            onDocTypeChange={setDocTypeIndic}
            onGenerate={handleGenerateXml}
            onDismissLastXml={() => dispatch(resetGenerateXmlStatus())}
            formatDate={formatDate}
          />
        )}

        {reports.length > 0 && (
          <MarkSubmittedCard
            reports={reports}
            submitReportId={submitReportId}
            submissionNotes={submissionNotes}
            markAsSubmittedStatus={markAsSubmittedStatus}
            onSubmitReportChange={setSubmitReportId}
            onSubmissionNotesChange={setSubmissionNotes}
            onMarkAsSubmitted={handleMarkAsSubmitted}
          />
        )}

        <PreviousReportsCard
          loadingStatus={loadingStatus}
          error={error}
          reports={reports}
          formatDate={formatDate}
          getStatusBadge={getStatusBadge}
        />
      </Container>
    </>
  );
}

export default Dac7Reports;
